import { redirect } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { getCurrentUser } from '@/lib/auth-client'
import { ApiError } from '@/lib/axios-client'
import { queryKeys } from '@/lib/query-keys'
import type { AccessState, User } from '@/types/user-type'

export type AuthRouteFlags = {
  isAuthRoute: boolean
  isInviteRoute: boolean
  isSuspendedRoute: boolean
  isAnyCitySetupRoute: boolean
  isCompleteProfileRoute: boolean
}

export function getAuthRouteFlags(pathname: string): AuthRouteFlags {
  const isContinueRegistrationRoute = pathname === '/auth/continue-registration'
  const isCitySetupRoute = pathname === '/auth/city-setup'

  return {
    isAuthRoute: pathname.startsWith('/auth'),
    isInviteRoute: pathname === '/auth/invite',
    isSuspendedRoute: pathname === '/auth/suspended-city',
    isAnyCitySetupRoute: isContinueRegistrationRoute || isCitySetupRoute,
    isCompleteProfileRoute: pathname === '/auth/complete-profile',
  }
}

export function normalizeAccessState(
  accessState: string | undefined,
): AccessState {
  if (!accessState) return 'active'
  return accessState.toLowerCase() as AccessState
}

export function getAccessStateRedirectTarget(
  user: User,
  flags: AuthRouteFlags,
): string | null {
  const accessState = normalizeAccessState(user.accessState)
  const hasDetails = user.hasDetails
  const shouldGoToInvite =
    accessState === 'invite_required' || accessState === 'pending_review'
  const isSuspendedCityUser = accessState === 'suspended'
  const isRejectedCityUser = accessState === 'rejected'
  const shouldGoToContinueRegistration = accessState === 'continue_registration'
  const shouldCompleteInvitedProfile =
    accessState === 'complete_invited_profile'

  if (shouldCompleteInvitedProfile && !flags.isCompleteProfileRoute) {
    return '/auth/complete-profile'
  }

  if (shouldGoToInvite && !flags.isInviteRoute) {
    return '/auth/invite'
  }

  if (flags.isInviteRoute && !shouldGoToInvite) {
    if (accessState === 'active') return '/'
    if (isRejectedCityUser) return '/auth/city-setup'
    if (isSuspendedCityUser) return '/auth/suspended-city'
    if (shouldGoToContinueRegistration) return '/auth/city-setup'
    if (user.isActive) return '/'
    return '/auth/city-setup'
  }

  if (
    isSuspendedCityUser &&
    !flags.isSuspendedRoute &&
    !flags.isAnyCitySetupRoute &&
    !flags.isInviteRoute
  ) {
    return '/auth/suspended-city'
  }

  if (isRejectedCityUser && !flags.isAnyCitySetupRoute) {
    return '/auth/city-setup'
  }

  if (shouldGoToContinueRegistration && !flags.isAnyCitySetupRoute) {
    return '/auth/city-setup'
  }

  const canStayOnAuthRoute =
    flags.isInviteRoute ||
    flags.isSuspendedRoute ||
    (flags.isCompleteProfileRoute && shouldCompleteInvitedProfile) ||
    (flags.isAnyCitySetupRoute &&
      (isSuspendedCityUser ||
        isRejectedCityUser ||
        shouldGoToContinueRegistration ||
        !hasDetails))

  if (
    (user.isActive || accessState === 'active') &&
    flags.isAuthRoute &&
    !canStayOnAuthRoute
  ) {
    return '/'
  }

  return null
}

export async function ensureAuthenticatedUser(
  queryClient: QueryClient,
): Promise<User | null> {
  try {
    return await queryClient.fetchQuery({
      queryKey: queryKeys.auth.user,
      queryFn: getCurrentUser,
      staleTime: 0,
    })
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized()) {
      return null
    }
    throw error
  }
}

export function throwAccessStateRedirect(
  user: User,
  flags: AuthRouteFlags,
): void {
  const target = getAccessStateRedirectTarget(user, flags)
  if (!target) return

  if (target === '/auth/invite') {
    throw redirect({
      to: '/auth/invite',
      search: { token: undefined, invitationToken: undefined },
      replace: true,
    })
  }

  throw redirect({ to: target, replace: true })
}
